import { useState } from "react";
import { observer } from "@legendapp/state/react";
import { Button } from "primereact/button";
import Avatar from "@/components/Avatar";
import ThemeToggle from "@/layouts/AppLayout/ThemeToggle";
import { logout } from "@/apis/clients/sessionClient";
import { AuthStore$ } from "@/stores/authStore";
import useClickOutside from "@/hooks/useClickOutside";
import { TEXT } from "@/constants/text";
import styles from "@/layouts/AppLayout/ProfileMenu.module.css";

const onSignOut = () => {
  logout().catch((error: unknown) => {
    // Staying put is the honest outcome: the session may still be live
    // server-side, and moving to the login page would tell the user it is
    // gone. logout() navigates on success, so there is nothing to undo here.
    // eslint-disable-next-line no-console
    console.error("could not sign out", error);
  });
};

interface ProfileMenuProps {
  collapsed?: boolean;
}

const ProfileMenu = observer(({ collapsed }: ProfileMenuProps) => {
  const session = AuthStore$.session.get();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useClickOutside<HTMLDivElement>(isOpen, () =>
    setIsOpen(false)
  );

  if (!session) return null;

  return (
    <div
      ref={rootRef}
      className={`${styles.profileMenu}${collapsed ? ` ${styles.profileMenuCollapsed}` : ""}`}
    >
      {isOpen && (
        <div
          className={`${styles.overlay}${collapsed ? ` ${styles.overlayCollapsed}` : ""}`}
        >
          <div className={styles.header}>
            <Avatar name={session.name} />
            <div className={styles.headerText}>
              <div className={styles.headerName}>{session.name}</div>
              <div className={styles.headerEmail}>{session.email}</div>
            </div>
          </div>
          <div className={styles.divider} />
          <ThemeToggle />
          <Button
            icon="pi pi-sign-out"
            label={TEXT.profileMenu.signOutButton}
            pt={{
              root: { className: styles.signOutButton },
              label: { className: styles.signOutLabel },
            }}
            onClick={onSignOut}
          />
        </div>
      )}

      <button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={collapsed ? session.name : undefined}
        className={styles.trigger}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
      >
        <Avatar name={session.name} />
        {!collapsed && (
          <span className={styles.triggerName}>{session.name}</span>
        )}
        {!collapsed && (
          <i
            aria-hidden="true"
            className={`pi ${isOpen ? "pi-chevron-up" : "pi-chevron-down"} ${styles.chevron}`}
          />
        )}
      </button>
    </div>
  );
});

export default ProfileMenu;
